import { consume } from '@lit/context';
import { LitElement, css, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { styleMap } from 'lit/directives/style-map.js';

import { gameApiContext } from '../../../context';
import type { GameAPIType } from '../../../GameAPI';
import { scrollbarThin } from '../../../theme/scrollbar.css';
import { tokens } from '../../../theme/tokens.css';
import { uiState as uiStateProxy } from '../../../UIState';
import { bindValtio } from '../../../ValtioController';
import { GAME_TYPE_META } from '../../game-types';

/** Pixels to nudge the panel up so its top edge aligns with the chip's
 *  visual top once the chip's 6px vertical padding is accounted for. */
const PANEL_TOP_OFFSET = -7;

/** Must match the `.selector { width: 230px }` rule below. Used by the
 *  position clamp so the panel doesn't flow off the right edge of the
 *  viewport when the chip ends up near the right side of the top-bar. */
const PANEL_WIDTH = 230;

/** Minimum gap between the fixed panel and the viewport edges. */
const PANEL_VIEWPORT_MARGIN = 8;

/** Window event that opens the list from outside the chip — dispatched by the
 *  menu drawer's Switch Game row. Open-only (not a toggle), so it can't shut a
 *  list that F2 already opened. */
export const OPEN_GAME_SELECTOR_EVENT = 'open-game-selector';

/**
 * Game Selector — in-game game/region chip + dropdown.
 *
 * Closed state is a small chip showing the current game and region; tapping
 * it (or the F2 keybind, or the menu drawer's Switch Game row) opens a panel
 * listing other games in the same region, grouped by type. Drives
 * `api.startGamesData` while open so the panel sees fresh player/bot counts
 * and ping data.
 *
 * @element game-selector
 */
@customElement('game-selector')
export class GameSelector extends LitElement {
  static override styles = [
    tokens,
    scrollbarThin,
    css`
      :host {
        display: block;
        position: relative;
        /* Declared here rather than inherited from the body — the component
           owns its own typography, and a slow tap on the list shouldn't start
           an iOS text selection, callout or grey tap flash. */
        font-family: var(--font-family);
        font-size: var(--font-size-13);
        text-shadow: var(--shadow-text);
        color: var(--color-text-white);
        -webkit-user-select: none;
        user-select: none;
        -webkit-touch-callout: none;
        -webkit-tap-highlight-color: transparent;
        z-index: 10;
        pointer-events: auto;
        flex: 0 0 auto;
      }

      /* This z-index makes the host a stacking context, so the fixed panel's
         own z-index only orders it inside the host — at 10 the kill notice and
         game notifications (z 50) painted over the open list. On mobile the
         host sits outside the top bar, so airmash-game's lift for an open
         top-bar popover doesn't reach it; lift the host itself while open. */
      :host([open]) {
        z-index: 200;
      }

      /* Closed state - menu button */
      .menu-button {
        display: inline-block;
        padding: 6px 10px;
        color: var(--color-text-tooltip);
        border-radius: var(--radius-md);
        background-color: var(--color-bg-chip);
        margin-right: 4px;
        cursor: pointer;
      }

      @media (hover: hover) {
        .menu-button:hover {
          color: var(--color-text-white);
          background-color: var(--color-bg-selector-hover);
        }

        .menu-button:hover .arrowdown {
          border-right-color: var(--color-text-white);
          border-bottom-color: var(--color-text-white);
        }
      }

      .menu-button:active {
        color: var(--color-text-white);
        background-color: var(--color-bg-selector-hover);
      }

      .arrowdown {
        display: inline-block;
        border-right: 3px solid var(--color-text-tooltip);
        border-bottom: 3px solid var(--color-text-tooltip);
        height: 5px;
        width: 5px;
        transform: rotate(45deg);
        margin-right: 8px;
        vertical-align: 2px;
      }

      .region {
        color: var(--color-text-muted);
      }

      /* Open state - selector panel.
         position:fixed positions against the viewport but does NOT escape an
         ancestor stacking context, so this z-index only orders the panel
         within the host. What actually lifts it above the game UI (z 50-100)
         is :host([open]) above, plus airmash-game's .top-bar.popover-open on
         desktop, both at 200: above every game-UI component but below the
         panel tier (300 in the app z ladder), so modals like the score panel
         still win. left/top are set inline from the chip's
         getBoundingClientRect(). */
      .selector {
        position: fixed;
        z-index: 200;
        width: 230px;
        background-color: var(--color-bg-panel);
        box-shadow: var(--shadow-panel);
        border-radius: var(--radius-lg);
        border-bottom: 2px solid var(--color-border-strong);
        border-top: 2px solid var(--color-border-light);
        padding: 12px 14px;
        cursor: default;
        /* Cap so the panel never overflows the viewport on portrait phones
           (where the games list can be longer than the screen). Scrolls
           internally beyond this. Subtract the panel's own fixed top offset
           (--selector-top, set inline next to top) so the cap tracks the
           actual position — on mobile top is 64px, so a flat 100dvh minus 30px
           would still let the bottom run ~34px offscreen. The 8px trailing gap
           matches PANEL_VIEWPORT_MARGIN. dvh handles the iOS URL bar collapse. */
        box-sizing: border-box;
        max-height: calc(100vh - var(--selector-top, 0px) - 8px);
        max-height: calc(100dvh - var(--selector-top, 0px) - 8px);
        overflow-y: auto;
        overscroll-behavior: contain;
      }

      .header {
        font-size: var(--font-size-16);
        font-weight: bold;
        color: var(--color-text-white);
        text-align: center;
      }

      .header .region {
        color: var(--color-text-tooltip);
      }

      .buttons {
        margin-top: 8px;
        margin-bottom: 12px;
        text-align: center;
      }

      .change-region {
        position: relative;
        display: inline-block;
        padding: 2px 6px;
        background-color: var(--color-btn-login);
        border-radius: var(--radius-sm);
        font-size: var(--font-size-xs);
        font-weight: bold;
        cursor: pointer;
        color: var(--color-text-white);
        text-shadow: var(--shadow-text-btn);
        border-bottom: 2px solid var(--color-border-soft);
      }

      /* Touch-only hit pad: the button is 111x20, under even the 24px minimum.
         Kept to 123x34 (the pad measures from inside the 2px bottom border),
         not 44 — it returns you to the lobby, so it must not catch taps aimed
         at the heading above. It fills the 8px gap up to the header exactly,
         and 6 of the 12px below. */
      @media (hover: none) {
        .change-region::after {
          content: '';
          position: absolute;
          inset: -8px -6px;
        }
      }

      @media (hover: hover) {
        .change-region:hover {
          background-color: var(--color-btn-login-hover);
          box-shadow: var(--shadow-btn-hover);
        }
      }

      .change-region:active {
        border-bottom: none;
        border-top: 2px solid var(--color-border-strong);
        background-color: var(--color-btn-login-hover);
      }

      /* Game list items */
      .section-header {
        margin-top: 8px;
        padding: 8px 0 8px 10px;
        font-size: var(--font-size-md);
        font-weight: bold;
        color: var(--color-text-white);
        white-space: nowrap;
      }

      /* Loading / no-games message in place of an empty games list. */
      .placeholder {
        padding: 14px 10px;
        text-align: center;
        color: var(--color-text-tertiary);
        font-style: italic;
      }

      /* Info icon is hover-only — hide on touch-primary where there's no
         way to surface the tooltip. */
      .info-icon {
        display: inline-block;
        width: 14px;
        height: 14px;
        margin-left: 6px;
        vertical-align: middle;
        background-repeat: no-repeat;
        background-size: 14px 14px;
        background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 32 32'%3E%3Cpath d='M16,0C24.777,0 32,7.223 32,16C32,24.777 24.777,32 16,32C7.223,32 0,24.777 0,16C0,7.223 7.223,0 16,0M19,15L13,15L13,26L19,26L19,15ZM16,6C14.354,6 13,7.354 13,9C13,10.646 14.354,12 16,12C17.646,12 19,10.646 19,9C19,7.354 17.646,6 16,6' fill='white'/%3E%3C/svg%3E");
        opacity: 0.5;
        cursor: help;
      }

      @media (hover: none) {
        .info-icon {
          display: none;
        }
      }

      @media (hover: hover) {
        .info-icon:hover {
          opacity: 1;
        }
      }

      /* Tooltip is position:fixed and rendered as a sibling of .selector
         so it escapes the panel's overflow:auto clipping. Coords are
         computed from the icon's bounding rect on mouseenter. */
      .tooltip {
        position: fixed;
        z-index: 250;
        width: 240px;
        background-color: var(--color-bg-tooltip);
        border-radius: 6px;
        border-bottom: 2px solid rgba(0, 0, 0, 0.25);
        border-top: 2px solid var(--color-border-subtle);
        padding: 10px 14px;
        box-sizing: border-box;
        white-space: normal;
        pointer-events: none;
        color: var(--color-text-link);
        font-weight: normal;
        font-size: var(--font-size-xs);
        line-height: 1.4;
      }

      .tooltip .arrow {
        position: absolute;
        top: 15px;
        left: -6px;
        width: 0;
        height: 0;
        border-top: 6px solid transparent;
        border-bottom: 6px solid transparent;
        border-right: 6px solid var(--color-bg-tooltip);
      }

      .game-item {
        display: flex;
        padding: 8px 0 8px 10px;
        border-radius: var(--radius-md);
      }

      .game-item.selectable {
        cursor: pointer;
      }

      @media (hover: hover) {
        .game-item.selectable:hover {
          background-color: var(--color-bg-item-hover);
        }

        .game-item.selectable:hover .game-name {
          color: var(--color-text-white);
        }
      }

      .game-item.selectable:active {
        background-color: var(--color-bg-item-hover);
      }

      .game-item.selected {
        background-color: var(--color-bg-selected);
      }

      .game-name {
        flex: 1;
        font-size: var(--font-size-xs);
        color: var(--color-text-primary);
        white-space: nowrap;
      }

      .game-item.selected .game-name {
        color: var(--color-text-white);
        font-weight: bold;
      }

      .player-count {
        width: 40px;
        text-align: right;
        font-size: var(--font-size-xs);
        color: var(--color-text-white);
      }

      .bot-count {
        width: 40px;
        padding-left: 10px;
        text-align: left;
        font-size: var(--font-size-xs);
        color: var(--color-text-muted);
      }

      .unknown {
        color: var(--color-text-secondary);
      }
    `,
  ];

  /** UI state from context */
  private uiState = bindValtio(this, uiStateProxy);

  /** GameAPI for actions */
  @consume({ context: gameApiContext })
  private api!: GameAPIType;

  /** Whether the selector panel is open */
  @state()
  private isOpen = false;

  /** Viewport-relative position for the fixed panel, computed from the chip's
   *  bounding rect when we open and on window resize. Both are CSS lengths
   *  rather than numbers so mobile can include the safe-area insets, which
   *  only CSS can read. */
  @state() private panelLeft = '0px';
  @state() private panelTop = '0px';

  /** Hover-tooltip state. typeNum is the game-type currently hovered (or null
   *  when no tooltip showing). Coords are viewport-relative since the tooltip
   *  is position:fixed to escape the panel's overflow clipping. */
  @state() private tooltipTypeNum: number | null = null;
  @state() private tooltipLeft = 0;
  @state() private tooltipTop = 0;

  override connectedCallback() {
    super.connectedCallback();
    document.addEventListener('click', this.handleClickOutside);
    window.addEventListener('toggle-game-selector', this.handleKeybindToggle);
    window.addEventListener(OPEN_GAME_SELECTOR_EVENT, this.handleOpenRequest);
    window.addEventListener('resize', this.handleResize);
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('click', this.handleClickOutside);
    window.removeEventListener('toggle-game-selector', this.handleKeybindToggle);
    window.removeEventListener(OPEN_GAME_SELECTOR_EVENT, this.handleOpenRequest);
    window.removeEventListener('resize', this.handleResize);
    // If the component is torn down while open (screen transition, parent
    // unmount), release external state so the slot doesn't stay taken and
    // games-data polling doesn't leak. Also reset internal state — Lit
    // custom elements can be detached and re-attached, and a reconnect with
    // stale isOpen / tooltipTypeNum would flash the panel + orphan tooltip
    // at the previous coords before willUpdate's cleanup catches it. No
    // sound — this isn't user-driven.
    if (this.isOpen) {
      this.isOpen = false;
      this.tooltipTypeNum = null;
      if (this.uiState.openTopBarPanel === 'game-selector') {
        this.api.setOpenTopBarPanel(null);
      }
      this.api.stopGamesData();
    }
  }

  /** Resnap the fixed panel to the chip after orientation/resize. */
  private handleResize = () => {
    if (this.isOpen) this.recomputePanelPosition();
  };

  private recomputePanelPosition() {
    if (this.uiState.isMobile) {
      // No chip on mobile — the logo triggers us, so there's nothing in the
      // flow to anchor to. Sit the panel at the top-left below the action
      // bar, matching game-log's offset — including its safe-area inset, so in
      // landscape on a notched phone the list clears the notch and lines up
      // with the logo that opened it, rather than starting 12px from the glass.
      // The top adds the status-bar inset, which moves the logo down too in an
      // installed app held upright.
      this.panelLeft = 'calc(12px + env(safe-area-inset-left, 0px))';
      this.panelTop = 'calc(64px + env(safe-area-inset-top, 0px))';
      return;
    }
    const rect = this.getBoundingClientRect();
    // Clamp so the panel stays fully inside the viewport even when the chip
    // ends up near the right edge of a cramped top-bar.
    const left = Math.max(
      PANEL_VIEWPORT_MARGIN,
      Math.min(rect.left, window.innerWidth - PANEL_WIDTH - PANEL_VIEWPORT_MARGIN),
    );
    this.panelLeft = `${left}px`;
    this.panelTop = `${Math.max(PANEL_VIEWPORT_MARGIN, rect.top + PANEL_TOP_OFFSET)}px`;
  }

  /** Show the type-description tooltip beside the hovered info-icon. The
   *  icon's `data-type-num` attribute selects which description to render. */
  private handleInfoEnter = (e: MouseEvent) => {
    const icon = e.currentTarget as HTMLElement;
    const typeNum = Number(icon.dataset['typeNum']);
    if (!Number.isFinite(typeNum)) return;
    const rect = icon.getBoundingClientRect();
    this.tooltipTypeNum = typeNum;
    // Tooltip sits to the right of the icon. -16 vertical offset matches the
    // arrow's 15px top so the pointer aligns with the icon centre.
    this.tooltipLeft = rect.right + 8;
    this.tooltipTop = rect.top - 16;
  };

  private handleInfoLeave = () => {
    this.tooltipTypeNum = null;
  };

  /** Close ourselves when a sibling top-bar panel becomes the active one, and
   *  mirror the open state onto the host for the :host([open]) lift. Before
   *  render, so the first open frame already paints above the notices. */
  protected override willUpdate() {
    if (this.isOpen && this.uiState.openTopBarPanel !== 'game-selector') {
      this.isOpen = false;
      this.tooltipTypeNum = null;
      this.api.stopGamesData();
    }
    this.toggleAttribute('open', this.isOpen);
  }

  /** Click on the chip. Stops the document-level outside-click handler from
   *  immediately closing the panel after we open it. The click sound lives on
   *  the taps (here and on a game row), not in open/close: F2 and an outside
   *  dismiss stay silent, like key toggles and the sibling popovers. */
  private handleClick = (e: Event) => {
    e.stopPropagation();
    this.api.playClick();
    this.toggleOpen();
  };

  /** Toggle event from the F2 keybind, and from airmash-logo on mobile (which
   *  plays its own click, being a tap). No event to stop, just flips state. */
  private handleKeybindToggle = () => {
    this.toggleOpen();
  };

  /** Open-only request from the menu drawer's Switch Game row
   *  (OPEN_GAME_SELECTOR_EVENT). Silent: the row plays its own click. */
  private handleOpenRequest = () => {
    if (!this.isOpen) this.open();
  };

  private toggleOpen() {
    if (this.isOpen) this.close();
    else this.open();
  }

  /** Open the selector and start games data service.
   *
   *  Note: `startGamesData` / `stopGamesData` aren't ref-counted. Safe here
   *  because game-selector (in-game) and logon-panel (lobby) live on
   *  different screens and never coexist. If a future consumer is added to
   *  the lobby/game overlap, this needs ref-counting in `GameAPI.ts`. */
  private open() {
    // Compute position BEFORE flipping isOpen so the fixed panel paints at
    // the right coords on its first frame.
    this.recomputePanelPosition();
    this.isOpen = true;
    this.api.setOpenTopBarPanel('game-selector');
    this.api.startGamesData();
  }

  private close() {
    this.isOpen = false;
    // Clear any hovered tooltip — the icon is about to be unrendered, so
    // its @mouseleave can't fire to clear this for us. Without this, the
    // tooltip would survive as an orphan at host level.
    this.tooltipTypeNum = null;
    if (this.uiState.openTopBarPanel === 'game-selector') {
      this.api.setOpenTopBarPanel(null);
    }
    this.api.stopGamesData();
  }

  /** Close menu when clicking outside */
  private handleClickOutside = (e: MouseEvent) => {
    // Use composedPath for shadow DOM compatibility
    const path = e.composedPath();
    if (this.isOpen && !path.includes(this)) {
      this.close();
    }
  };

  private handleSelectGame(gameServerId: string) {
    this.api.playClick();
    this.close();
    this.api.switchGame(gameServerId);
  }

  /** Stable click handler for game items — identity doesn't change across
   *  re-renders so Lit doesn't rebind on every games-data refresh tick. */
  private handleGameItemClick = (e: MouseEvent) => {
    const id = (e.currentTarget as HTMLElement).dataset['gameServerId'];
    if (!id) return;
    this.handleSelectGame(id);
  };

  /** Stable variant of `(e) => e.stopPropagation()` for the inner panel
   *  click trap — same rebinding concern. */
  private stopClickPropagation = (e: Event) => {
    e.stopPropagation();
  };

  private handleChangeRegion = () => {
    this.api.playClick();
    this.api.changeRegion();
  };

  /** The region whose games the open panel lists. Normally the one you're in
   *  (currentRegionId), but if a poll ever returns data without it — or it isn't
   *  resolved yet — fall back to the closest, then the first region, so the panel
   *  shows games instead of only CHANGE REGION. null only when no region data has
   *  loaded at all (→ the Loading… placeholder). */
  private get displayRegion() {
    const { regions, currentRegionId, closestRegionId } = this.uiState;
    if (regions.length === 0) return null;
    return (
      regions.find((r) => r.id === currentRegionId) ??
      regions.find((r) => r.id === closestRegionId) ??
      regions[0] ??
      null
    );
  }

  /** Display region's games grouped by type, sorted numerically, with each
   *  group carrying its display name and description. */
  private get gamesGroupedByType() {
    const region = this.displayRegion;
    if (!region) return [];

    const groups = new Map<number, typeof region.games>();
    for (const game of region.games) {
      const typeNum = Number(game.type);
      if (!groups.has(typeNum)) groups.set(typeNum, []);
      groups.get(typeNum)!.push(game);
    }

    return Array.from(groups.entries())
      .sort(([a], [b]) => a - b)
      .map(([typeNum, games]) => ({
        typeNum,
        typeName: GAME_TYPE_META[typeNum]?.name ?? `Type ${typeNum}`,
        typeDesc: GAME_TYPE_META[typeNum]?.description ?? '',
        games,
      }));
  }

  private renderClosed() {
    const gameName = this.uiState.currentGameName ?? '...';
    const regionName = this.uiState.currentRegionName ?? '';

    return html`
      <div
        class="menu-button"
        role="button"
        tabindex="0"
        aria-haspopup="listbox"
        aria-expanded=${this.isOpen}
        aria-label="Game: ${gameName}, region: ${regionName}"
        @click=${this.handleClick}
      >
        <span class="arrowdown"></span>
        ${gameName}
        <span class="region">&nbsp;&bull;&nbsp;${regionName}</span>
      </div>
    `;
  }

  private renderOpen() {
    const gameName = this.uiState.currentGameName ?? '...';
    const regionName = this.uiState.currentRegionName ?? '';
    // Use the *display* region's id for the switch ids below, so a game picked
    // from a fallback region (when currentRegionId isn't in the data) builds a
    // gameServerId switchGame can resolve.
    const regionId = this.displayRegion?.id ?? this.uiState.currentRegionId;
    const currentGameId = this.uiState.currentGameId;
    const groupedGames = this.gamesGroupedByType;

    return html`
      <div
        class="selector scrollbar-thin"
        role="listbox"
        aria-label="Switch game"
        style=${styleMap({
          left: this.panelLeft,
          top: this.panelTop,
          '--selector-top': this.panelTop,
        })}
        @click=${this.stopClickPropagation}
      >
        <div class="header">
          ${gameName}
          <span class="region">&nbsp;&bull;&nbsp;${regionName}</span>
        </div>
        <div class="buttons">
          <div class="change-region" role="button" tabindex="0" @click=${this.handleChangeRegion}>CHANGE REGION</div>
        </div>

        ${
          groupedGames.length === 0
            ? html`<div class="placeholder">
                ${this.uiState.regions.length === 0 ? 'Loading…' : 'No games available'}
              </div>`
            : groupedGames.map(
                (group) => html`
                  <div class="section-header" role="group" aria-label=${group.typeName}>
                    ${group.typeName}
                    ${
                      group.typeDesc
                        ? html`
                            <span
                              class="info-icon"
                              data-type-num=${group.typeNum}
                              aria-label="${group.typeName} description"
                              @mouseenter=${this.handleInfoEnter}
                              @mouseleave=${this.handleInfoLeave}
                            ></span>
                          `
                        : nothing
                    }
                  </div>
                  ${group.games.map((game) => {
                    const isSelected = game.id === currentGameId;
                    const gameServerId = `${regionId}-${game.id}`;
                    const playerCount = game.players != null ? Math.max(0, game.players - (game.bots ?? 0)) : null;
                    const displayName = game.nameShort ?? game.name;

                    return html`
                      <div
                        class="game-item ${isSelected ? 'selected' : 'selectable'}"
                        role="option"
                        aria-selected=${isSelected}
                        data-game-server-id=${isSelected ? nothing : gameServerId}
                        @click=${isSelected ? undefined : this.handleGameItemClick}
                      >
                        <span class="game-name">${displayName}</span>
                        <span class="player-count">
                          ${playerCount != null ? playerCount : html`<span class="unknown">?</span>`}
                        </span>
                        <span class="bot-count">${game.bots ? `+${game.bots}` : ''}</span>
                      </div>
                    `;
                  })}
                `,
              )
        }
      </div>
    `;
  }

  /** Description tooltip — rendered at :host level (sibling of .selector)
   *  with position:fixed so the panel's overflow:auto can't clip it. */
  private renderTooltip() {
    if (this.tooltipTypeNum === null) return nothing;
    const desc = GAME_TYPE_META[this.tooltipTypeNum]?.description;
    if (!desc) return nothing;
    return html`
      <div
        class="tooltip"
        role="tooltip"
        style=${styleMap({ left: `${this.tooltipLeft}px`, top: `${this.tooltipTop}px` })}
      >
        <div class="arrow"></div>
        ${desc}
      </div>
    `;
  }

  override render() {
    // Desktop: render the chip so the host keeps its width in the flex row;
    // the open panel is `position: fixed` and overlays it without affecting
    // flow. Mobile: the chip is dropped (the logo is the trigger) — the host
    // collapses to zero width but stays mounted to own the panel + the
    // `toggle-game-selector` listener.
    return html`
      ${this.uiState.isMobile ? nothing : this.renderClosed()} ${this.isOpen ? this.renderOpen() : nothing}
      ${this.renderTooltip()}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'game-selector': GameSelector;
  }
}
